/**
 * Appendix A.2 / §14.5 — fixed-timestep driver.
 * Sim runs at exactly 60 Hz; render is decoupled and interpolates with alpha.
 */

export const STEP = 1 / 60;
export const STEP_MS = 1000 / 60;
export const MAX_STEPS = 5;

export interface LoopHooks {
  /** Sample buffered inputs for this tick. */
  sampleInput(tick: number): void;
  /** Pure sim step: no wall clock, no DOM. */
  step(tick: number): void;
  /** Drain the event queue for this tick. */
  flush(tick: number): void;
  /** Render; alpha ∈ [0,1). Never mutates sim state. */
  render(tick: number, alpha: number): void;
  /** Sim-time dilation for this frame (hit-stop, §03.10). Function of sim events only. */
  dilation(): number;
  /** Called when the spiral guard drops steps. */
  onStepsDropped?(count: number): void;
}

export class FixedLoop {
  private acc = 0;
  private tick = 0;
  private running = false;
  private rafId = 0;
  private lastTime = 0;
  stepsDropped = 0;
  /** Wall-clock ms spent inside sim.step() last frame (dev/bench only). */
  lastSimMs = 0;
  lastFrameMs = 0;
  private timing = false;

  constructor(private hooks: LoopHooks) {}

  enableTiming(v: boolean): void {
    this.timing = v;
  }

  get currentTick(): number {
    return this.tick;
  }

  setTick(t: number): void {
    this.tick = t >>> 0;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = 0;
    this.acc = 0;
    const frame = (now: number) => {
      if (!this.running) return;
      this.rafId = requestAnimationFrame(frame);
      const dt = this.lastTime === 0 ? STEP_MS : now - this.lastTime;
      this.lastTime = now;
      this.advance(dt);
    };
    this.rafId = requestAnimationFrame(frame);
  }

  stop(): void {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  get isRunning(): boolean {
    return this.running;
  }

  /** Drives one frame with a raw delta in ms. Exposed for headless/bench use. */
  advance(rawDtMs: number): void {
    const frameStart = this.timing ? performance.now() : 0;
    this.acc += (Math.min(rawDtMs, 250) / 1000) * this.hooks.dilation();
    let steps = 0;
    const simStart = this.timing ? performance.now() : 0;
    while (this.acc >= STEP && steps < MAX_STEPS) {
      this.hooks.sampleInput(this.tick);
      this.hooks.step(this.tick);
      this.hooks.flush(this.tick);
      this.acc -= STEP;
      this.tick = (this.tick + 1) >>> 0;
      steps++;
    }
    if (this.timing) this.lastSimMs = performance.now() - simStart;
    if (this.acc >= STEP) {
      const dropped = Math.floor(this.acc / STEP);
      this.stepsDropped += dropped;
      this.hooks.onStepsDropped?.(dropped);
      this.acc = 0;
    }
    this.hooks.render(this.tick, this.acc / STEP);
    if (this.timing) this.lastFrameMs = performance.now() - frameStart;
  }

  /** Headless: drive N ticks with a synthetic 16.666 ms clock. */
  runHeadless(ticks: number): void {
    for (let i = 0; i < ticks; i++) this.advance(STEP_MS);
  }

  reset(): void {
    this.acc = 0;
    this.tick = 0;
    this.stepsDropped = 0;
  }
}
